import React, { useState, useEffect, useCallback } from 'react';
import { Input, Select, DatePicker, Button, Spin, message, Divider } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, CloseOutlined, CameraOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import dayjs from 'dayjs';
import { getExpenseNamesApi, createExpenseApi, updateExpenseApi } from '../../api/expenses';
import { getAccountsApi, createAccountApi, getAccountByIdApi } from '../../api/accounts';
import { EXPENSE_PHOTO } from '../../utils/constants';
import LabourPickerDialog from '../../components/ExpenseForm/LabourPickerDialog';
import { decryptData } from '../../utils/crypto';

const LABOUR_COMMON_IDS = [7, 8];
const STORE_COMMON_IDS  = [2, 3, 5, 6];
const OTHER_COMMON_ID   = 14;

const PAYMENT_OPTIONS = [
  { value: 'paid',    label: 'Paid' },
  { value: 'unpaid',  label: 'Unpaid' },
  { value: 'partial', label: 'Partial' },
];

function today() {
  return new Date().toISOString().split('T')[0];
}

function toLocalISOString(dateStr) {
  const now = new Date();
  const d = new Date(`${dateStr}T${now.toTimeString().split(' ')[0]}`);
  const offset = -d.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const pad = (n) => String(Math.abs(n)).padStart(2, '0');
  const hh = pad(Math.floor(Math.abs(offset) / 60));
  const mm = pad(Math.abs(offset) % 60);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}${sign}${hh}:${mm}`;
}

function initForm(editData) {
  if (editData) {
    return {
      expenseType:      editData.kharcho ? { name: editData.kharcho, common_id: editData.common_id ?? null, accountLink: editData.account_link || '' } : null,
      otherExpenseType: '',
      amount:           editData.amount || '',
      paymentStatus:    editData.payment_status || 'paid',
      storeId:          editData.pay_to_account ?? null,
      labours:          [],
      note:             editData.details || '',
      date:             editData.expense_date || today(),
      photo:            null,
      receivedAmount:   editData.received_amount || '',
      paymentImage:     null,
      bankAccountId:    editData.bank_account_id ?? null,
    };
  }
  return {
    expenseType: null, otherExpenseType: '', amount: '',
    paymentStatus: 'paid', storeId: null, labours: [],
    note: '', date: today(), photo: null, receivedAmount: '', paymentImage: null,
    bankAccountId: null,
  };
}

const fieldLabel = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 6,
};

const hintStyle = { fontSize: 12, color: '#9ca3af', fontWeight: 400, marginLeft: 6 };

function PhotoPicker({ preview, label, hintText, onChange, onClear }) {
  return (
    <div>
      {label && (
        <div style={fieldLabel}>
          {label}{hintText && <span style={hintStyle}>{hintText}</span>}
        </div>
      )}
      <label style={{ display: 'block', cursor: 'pointer' }}>
        {preview ? (
          <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            <img src={preview} alt="preview" style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }} />
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.45)', padding: '6px 0', textAlign: 'center' }}>
              <span style={{ color: '#fff', fontSize: 12, fontWeight: 500 }}>Click to change</span>
            </div>
            {onClear && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); onClear(); }}
                style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', width: 26, height: 26, cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <CloseOutlined style={{ fontSize: 11 }} />
              </button>
            )}
          </div>
        ) : (
          <div style={{ border: '2px dashed #d1d5db', borderRadius: 10, background: '#fafafa', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 100, gap: 8 }}>
            <CameraOutlined style={{ fontSize: 24, color: '#9ca3af' }} />
            <span style={{ fontSize: 12, color: '#9ca3af' }}>Click to upload photo</span>
          </div>
        )}
        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={onChange} />
      </label>
    </div>
  );
}

export default function AddExpensePage() {
  const navigate = useNavigate();
  const { cropId } = useParams();
  const location = useLocation();
  const editData = location.state?.editData ?? null;
  const { t } = useTranslation();

  const [form, setForm]                       = useState(() => initForm(editData));
  const [expenseNames, setExpenseNames]        = useState([]);
  const [expenseSearch, setExpenseSearch]      = useState('');
  const [expenseListOpen, setExpenseListOpen]  = useState(false);
  const [loadingNames, setLoadingNames]        = useState(false);
  const [accounts, setAccounts]               = useState([]);
  const [labourPickerOpen, setLabourPickerOpen] = useState(false);
  const [saving, setSaving]                   = useState(false);
  const [photoPreview, setPhotoPreview]        = useState(null);
  const [paymentImagePreview, setPaymentImagePreview] = useState(null);
  const [bankBalances, setBankBalances]        = useState({});

  const commonId = form.expenseType?.common_id ?? null;
  const isLabour = LABOUR_COMMON_IDS.includes(commonId) || form.expenseType?.accountLink === 'labour';
  const isStore  = STORE_COMMON_IDS.includes(commonId)  || form.expenseType?.accountLink === 'shop_keeper';
  const isOther  = commonId === OTHER_COMMON_ID;

  useEffect(() => {
    if (!isLabour) return;
    const total = form.labours.reduce((s, l) => s + (+l.amount || 0), 0);
    setForm((f) => ({ ...f, amount: total }));
  }, [form.labours, isLabour]);

  useEffect(() => {
    setPhotoPreview(editData?.photo ? EXPENSE_PHOTO(editData.photo) : null);
    setLoadingNames(true);
    Promise.all([
      getExpenseNamesApi({ lang: i18n.language, expense: '' }),
      getAccountsApi(),
    ]).then(([namesRes, accRes]) => {
      setExpenseNames(namesRes.data || []);
      const accs = accRes.data || [];
      setAccounts(accs);
      const bankAccs = accs.filter((a) => a.type === 'bank');
      Promise.all(bankAccs.map((a) => getAccountByIdApi(a.id, { type: 'bank' }).then((res) => {
        const txns = res.data?.transactions || [];
        const credit = txns.filter((t) => t.payment_status === 'credit').reduce((s, t) => s + Number(t.amount), 0);
        const debit  = txns.filter((t) => t.payment_status === 'debit').reduce((s, t) => s + Number(t.amount), 0);
        return { id: a.id, balance: credit - debit };
      }).catch(() => ({ id: a.id, balance: null })))).then((results) => {
        setBankBalances(Object.fromEntries(results.map((r) => [r.id, r.balance])));
      });
    }).catch(() => {}).finally(() => setLoadingNames(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = useCallback((key, val) => setForm((f) => ({ ...f, [key]: val })), []);

  const filteredNames = expenseNames.filter((e) =>
    expenseSearch === '' || e.name.toLowerCase().includes(expenseSearch.toLowerCase())
  );

  const labourAccounts     = accounts.filter((a) => a.type === 'labour');
  const shopkeeperAccounts = accounts.filter((a) => a.type === 'shop_keeper' && a.name?.toLowerCase() !== 'default');
  const bankAccounts       = accounts.filter((a) => a.type === 'bank');
  const showBankPicker     = !isLabour && bankAccounts.length > 0 && (form.paymentStatus === 'paid' || form.paymentStatus === 'partial');

  const handleSelectExpense = (item) => {
    const isStoreType = STORE_COMMON_IDS.includes(item.common_id);
    set('expenseType', { id: item.id, common_id: item.common_id, name: item.name });
    set('labours', []);
    set('storeId', null);
    set('amount', '');
    if (isStoreType) set('paymentStatus', 'paid');
    setExpenseSearch('');
    setExpenseListOpen(false);
  };

  const handleLabourAdd = (picked) => {
    const newRows = picked.map((a) => ({ account_id: a.id, name: a.name, amount: '', paymentStatus: 'paid' }));
    setForm((f) => ({ ...f, labours: [...f.labours, ...newRows] }));
    setLabourPickerOpen(false);
  };

  const updateLabour = (idx, field, val) => {
    setForm((f) => {
      const labours = f.labours.map((l, i) => i === idx ? { ...l, [field]: val } : l);
      return { ...f, labours };
    });
  };

  const removeLabour = (idx) => {
    setForm((f) => ({ ...f, labours: f.labours.filter((_, i) => i !== idx) }));
  };

  const partialExceedsTotal =
    !isLabour &&
    form.paymentStatus === 'partial' &&
    Number(form.receivedAmount) > 0 &&
    Number(form.amount) > 0 &&
    Number(form.receivedAmount) > Number(form.amount);

  const isValid =
    form.expenseType !== null &&
    Number(form.amount) > 0 &&
    form.date !== '' &&
    (!isOther || form.otherExpenseType.trim() !== '') &&
    (!(!isLabour && form.paymentStatus === 'partial') || Number(form.receivedAmount) > 0) &&
    !partialExceedsTotal;

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      const localDate       = toLocalISOString(form.date);
      const expenseTypeName = isOther ? form.otherExpenseType : form.expenseType.name;

      let storeId     = form.storeId;
      let accountLink = isLabour ? 'labour' : isStore ? 'shop_keeper' : '';
      if (!isLabour && !storeId) {
        let defaultAcc = accounts.find((a) => a.type === 'shop_keeper' && a.name?.toLowerCase() === 'default');
        if (!defaultAcc) {
          try {
            const res = await createAccountApi({ name: 'default', type: 'shop_keeper' });
            defaultAcc = res.data?.data || res.data;
            if (defaultAcc) setAccounts((prev) => [...prev, defaultAcc]);
          } catch {}
        }
        if (defaultAcc) { storeId = defaultAcc.id; accountLink = 'shop_keeper'; }
      }

      const fd = new FormData();
      fd.append('expenseType',  expenseTypeName);
      fd.append('amount',       Number(form.amount).toFixed(2));
      fd.append('note',         form.note);
      fd.append('date',         localDate);
      fd.append('cropId',       cropId);
      fd.append('storeId',      storeId ?? '');
      fd.append('account_link', accountLink);
      if (!isLabour) {
        fd.append('payment_status', form.paymentStatus);
        if (form.paymentStatus === 'partial' && form.receivedAmount) {
          fd.append('received_amount', Number(form.receivedAmount).toFixed(2));
          if (form.paymentImage) fd.append('receipt_photo', form.paymentImage);
        }
      }

      form.labours.forEach((l, i) => {
        fd.append(`labour[${i}][account_id]`,    l.account_id);
        fd.append(`labour[${i}][amount]`,        Number(l.amount).toFixed(2));
        fd.append(`labour[${i}][paymentStatus]`, l.paymentStatus);
        fd.append(`labour[${i}][date]`,          localDate);
        fd.append(`labour[${i}][expenseType]`,   expenseTypeName);
        fd.append(`labour[${i}][note]`,          form.note);
        fd.append(`labour[${i}][cropId]`,        cropId);
      });

      if (form.photo) fd.append('photo', form.photo);
      if (form.bankAccountId) fd.append('bank_account_id', form.bankAccountId);
      if (editData?.id) {
        fd.append('id',         editData.id);
        fd.append('old_amount', editData.amount);
      }

      if (editData?.id) {
        await updateExpenseApi(fd);
        message.success('Expense updated!');
      } else {
        await createExpenseApi(fd);
        message.success('Expense added!');
      }
      navigate(-1);
    } catch {
      message.error(editData?.id ? 'Failed to update expense' : 'Failed to add expense');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div style={{ margin: -24, minHeight: 'calc(100vh - 64px)', background: '#fff' }}>

        {/* ── Sticky page header ── */}
        <div style={{
          position: 'sticky', top: 64, zIndex: 100,
          background: '#fff',
          borderBottom: '1px solid #e5e7eb',
          padding: '0 40px',
          height: 56,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => navigate(-1)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 6, color: '#6b7280', display: 'flex', alignItems: 'center' }}
            >
              <ArrowLeftOutlined style={{ fontSize: 15 }} />
            </button>
            <span style={{ color: '#9ca3af', fontSize: 13 }}>/</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
              {editData?.id ? 'Edit Expense' : 'Add Expense'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={() => navigate(-1)}>Cancel</Button>
            <Button
              type="primary" loading={saving} disabled={!isValid} onClick={handleSave}
              style={{ background: '#1a6b2a', borderColor: '#1a6b2a', fontWeight: 600 }}
            >
              Save
            </Button>
          </div>
        </div>

        {/* ── Form body ── */}
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '40px 40px 80px' }}>

          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>
            {editData?.id ? 'Edit Expense' : 'Add Expense'}
          </h1>
          <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 36px' }}>
            Fill in the details below to record your crop expense.
          </p>

          {/* ── Expense Category ── */}
          <div style={{ marginBottom: 28 }}>
            <label style={fieldLabel}>Expense Category</label>
            {form.expenseType ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: '#f0fdf4', border: '1.5px solid #16a34a', borderRadius: 8 }}>
                <span style={{ flex: 1, fontWeight: 600, color: '#14532d', fontSize: 14 }}>
                  {isOther ? form.otherExpenseType || form.expenseType.name : form.expenseType.name}
                </span>
                <CloseOutlined
                  style={{ cursor: 'pointer', color: '#6b7280', fontSize: 12 }}
                  onClick={() => { set('expenseType', null); set('otherExpenseType', ''); }}
                />
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <Input
                  size="large"
                  prefix={loadingNames ? <Spin size="small" /> : null}
                  placeholder="Search expense category (e.g. Seeds, Fertiliser, Labour)..."
                  value={expenseSearch}
                  onChange={(e) => setExpenseSearch(e.target.value)}
                  onFocus={() => setExpenseListOpen(true)}
                  style={{ borderRadius: 8 }}
                />
                {expenseListOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 300,
                    marginTop: 4, maxHeight: 260, overflowY: 'auto',
                    border: '1px solid #e5e7eb', borderRadius: 8,
                    background: '#fff', boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  }}>
                    {filteredNames.length === 0 ? (
                      <div style={{ padding: '12px 16px', color: '#9ca3af', fontSize: 13 }}>
                        {loadingNames ? 'Loading…' : 'No results'}
                      </div>
                    ) : filteredNames.map((e) => (
                      <div
                        key={e.id}
                        onMouseDown={() => handleSelectExpense(e)}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                        onMouseEnter={(ev) => ev.currentTarget.style.background = '#f0fdf4'}
                        onMouseLeave={(ev) => ev.currentTarget.style.background = 'transparent'}
                      >
                        {e.image && <img src={e.image} alt={e.name} style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'cover' }} />}
                        <span style={{ fontSize: 14, color: '#111827' }}>{e.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {isOther && (
            <div style={{ marginBottom: 28 }}>
              <label style={fieldLabel}>Custom Expense Name</label>
              <Input
                size="large" placeholder="Enter expense name..."
                value={form.otherExpenseType}
                onChange={(e) => set('otherExpenseType', e.target.value)}
                style={{ borderRadius: 8 }}
              />
            </div>
          )}

          <Divider style={{ margin: '0 0 28px' }} />

          {/* ── Labour ── */}
          {isLabour && (
            <>
              <div style={{ marginBottom: 28 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <label style={{ ...fieldLabel, marginBottom: 0 }}>Labour Workers</label>
                  <Button size="small" icon={<PlusOutlined />} onClick={() => setLabourPickerOpen(true)} style={{ borderColor: '#1a6b2a', color: '#1a6b2a' }}>
                    Add
                  </Button>
                </div>
                {form.labours.length === 0 ? (
                  <div style={{ padding: '20px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13, border: '1px dashed #d1d5db', borderRadius: 8 }}>
                    No labour added — click Add to select workers
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {form.labours.map((l, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fafafa' }}>
                        <span style={{ flex: 1, fontWeight: 600, fontSize: 13, color: '#111827' }}>{l.name}</span>
                        <Input type="number" min={0} placeholder="Amount" value={l.amount} onChange={(e) => updateLabour(idx, 'amount', e.target.value)} prefix="₹" style={{ width: 140, borderRadius: 6 }} />
                        <Select size="small" value={l.paymentStatus} onChange={(v) => updateLabour(idx, 'paymentStatus', v)} options={PAYMENT_OPTIONS} style={{ width: 100 }} />
                        <CloseOutlined style={{ color: '#ef4444', cursor: 'pointer', fontSize: 12 }} onClick={() => removeLabour(idx)} />
                      </div>
                    ))}
                    <div style={{ textAlign: 'right', paddingTop: 4, fontWeight: 700, fontSize: 15, color: '#d97706' }}>
                      Total: ₹{Number(form.amount).toLocaleString()}
                    </div>
                  </div>
                )}
              </div>
              <Divider style={{ margin: '0 0 28px' }} />
            </>
          )}

          {/* ── Amount & Payment ── */}
          {!isLabour && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
                <div>
                  <label style={fieldLabel}>Total Amount</label>
                  <Input size="large" type="number" min={0} prefix="₹" placeholder="0.00" value={form.amount} onChange={(e) => set('amount', e.target.value)} style={{ borderRadius: 8 }} />
                </div>
                <div>
                  <label style={fieldLabel}>Payment Status</label>
                  <Select
                    size="large" style={{ width: '100%' }} value={form.paymentStatus}
                    onChange={(v) => {
                      set('paymentStatus', v);
                      if (v !== 'partial') set('receivedAmount', '');
                      if (v === 'unpaid') set('bankAccountId', null);
                    }}
                    options={PAYMENT_OPTIONS}
                    disabled={!!editData?.id && isStore}
                  />
                </div>
              </div>

              {form.paymentStatus === 'partial' && (
                <div style={{ marginBottom: 28 }}>
                  <label style={fieldLabel}>Partially Paid Amount <span style={hintStyle}>amount already paid</span></label>
                  <Input
                    size="large" type="number" min={0} prefix="₹" placeholder="0.00"
                    value={form.receivedAmount}
                    onChange={(e) => set('receivedAmount', e.target.value)}
                    disabled={!!editData?.id}
                    status={partialExceedsTotal ? 'error' : ''}
                    style={{ borderRadius: 8 }}
                  />
                  {partialExceedsTotal && (
                    <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>
                      Cannot exceed total amount (₹{Number(form.amount).toLocaleString()})
                    </div>
                  )}
                </div>
              )}

              {isStore && (
                <div style={{ marginBottom: 28 }}>
                  <label style={fieldLabel}>Vendor Account <span style={hintStyle}>shop or person you're paying</span></label>
                  <Select size="large" showSearch allowClear placeholder="Select vendor..." style={{ width: '100%' }} value={form.storeId} onChange={(v) => set('storeId', v)} filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())} options={shopkeeperAccounts.map((a) => ({ value: a.id, label: a.name }))} />
                </div>
              )}

              {showBankPicker && (
                <div style={{ marginBottom: 28 }}>
                  <label style={fieldLabel}>Bank Account <span style={hintStyle}>account used for this payment</span></label>
                  <Select
                    size="large" allowClear placeholder="Select bank account (optional)" style={{ width: '100%' }} value={form.bankAccountId} onChange={(v) => set('bankAccountId', v ?? null)}
                    options={bankAccounts.map((a) => {
                      const dec = a.bank_details ? (decryptData(a.bank_details) || {}) : {};
                      const balance = bankBalances[a.id] ?? null;
                      return { value: a.id, label: (a.name || dec.bank_name || 'Bank Account') + (dec.bank_name && a.name ? ` — ${dec.bank_name}` : ''), balance };
                    })}
                    optionRender={(opt) => (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{opt.data.label}</span>
                        {opt.data.balance !== null && (
                          <span style={{ fontSize: 12, fontWeight: 600, color: opt.data.balance >= 0 ? '#16a34a' : '#ef4444' }}>
                            {opt.data.balance >= 0 ? '' : '−'}₹{Math.abs(opt.data.balance).toLocaleString()}
                          </span>
                        )}
                      </div>
                    )}
                  />
                </div>
              )}

              <Divider style={{ margin: '0 0 28px' }} />
            </>
          )}

          {/* ── Date & Note ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
            <div>
              <label style={fieldLabel}>Date</label>
              <DatePicker size="large" style={{ width: '100%', borderRadius: 8 }} value={form.date ? dayjs(form.date) : null} onChange={(d) => set('date', d ? d.format('YYYY-MM-DD') : '')} />
            </div>
            <div>
              <label style={fieldLabel}>Note <span style={hintStyle}>optional</span></label>
              <Input size="large" placeholder="Add any additional notes here..." value={form.note} onChange={(e) => set('note', e.target.value)} style={{ borderRadius: 8 }} />
            </div>
          </div>

          {/* ── Photos ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 40 }}>
            <PhotoPicker
              label="Product or Receipt Photo"
              hintText="optional"
              preview={photoPreview}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                set('photo', file);
                setPhotoPreview(URL.createObjectURL(file));
              }}
              onClear={() => { set('photo', null); setPhotoPreview(null); }}
            />
            {!isLabour && form.paymentStatus === 'partial' && (
              <PhotoPicker
                label="Payment Receipt"
                hintText="optional"
                preview={paymentImagePreview}
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  set('paymentImage', file);
                  setPaymentImagePreview(file ? URL.createObjectURL(file) : null);
                  e.target.value = '';
                }}
                onClear={() => { set('paymentImage', null); setPaymentImagePreview(null); }}
              />
            )}
          </div>

          {/* ── Actions ── */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 8, borderTop: '1px solid #e5e7eb' }}>
            <Button size="large" onClick={() => navigate(-1)} style={{ minWidth: 100, borderRadius: 8 }}>Cancel</Button>
            <Button size="large" type="primary" loading={saving} disabled={!isValid} onClick={handleSave} style={{ background: '#1a6b2a', borderColor: '#1a6b2a', minWidth: 140, borderRadius: 8, fontWeight: 600 }}>
              Save Expense
            </Button>
          </div>

        </div>
      </div>

      <LabourPickerDialog
        open={labourPickerOpen}
        onClose={() => setLabourPickerOpen(false)}
        onConfirm={handleLabourAdd}
        labourAccounts={labourAccounts}
        excludeIds={form.labours.map((l) => l.account_id)}
        onLabourCreated={(newAccount) => {
          if (newAccount) setAccounts((prev) => [...prev, newAccount]);
        }}
      />
    </>
  );
}
